import React, { useState, useEffect, useCallback } from 'react';
import {
    Database,
    Globe,
    Plus,
    Edit,
    Trash2,
    ToggleLeft,
    ToggleRight,
    X,
    AlertTriangle,
    RefreshCw,
    Search,
    SearchX,
    Eye,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
    FileText,
    Calendar,
    Download,
    Layers,
    Tag,
    Key,
    Info,
    Copy,
    Check
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '../../../utils/toast';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { websiteSourceService, leadStatusService, leadSourceService } from '../../../api/crm/config/configService';
import { formatDate } from '../../../utils/stringUtils';
import { exportToCSV } from '../../../utils/exportUtils';

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 100;
const CODE_MIN_LENGTH = 2;
const CODE_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 1000;
const API_KEY_MIN_LENGTH = 32;
const API_KEY_MAX_LENGTH = 100;

// Custom debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

export default function WebsiteMaster() {
    const queryClient = useQueryClient();

    // Search & Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

    // Selection State
    const [selectedRows, setSelectedRows] = useState(new Set());

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('create'); // 'create' | 'edit'
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        domain: '',
        websiteUrl: '',
        apiKey: '',
        isActive: true,
        active: true,
        defaultLeadStatus: '',
        defaultLeadSource: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [isCodeManuallyEdited, setIsCodeManuallyEdited] = useState(false);

    // View Modal State
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingItem, setViewingItem] = useState(null);
    const [showApiKey, setShowApiKey] = useState(false);
    const [copiedApiKey, setCopiedApiKey] = useState(false);

    // Delete Modals State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingItem, setDeletingItem] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

    // Export Modal State
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    // Reset pagination on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, statusFilter, dateRange]);

    // Reset selection when filters change
    useEffect(() => {
        setSelectedRows(new Set());
    }, [debouncedSearch, statusFilter, dateRange]);

    // --- Queries ---
    const { data: allWebsiteSources } = useQuery({
        queryKey: ['websiteSources', 'all'],
        queryFn: () => websiteSourceService.getAll({})
    });
    const totalWebsiteSources = allWebsiteSources?.data?.length || 0;

    const { data: websiteSourcesData, isLoading: loadingWebsiteSources } = useQuery({
        queryKey: ['websiteSources', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => websiteSourceService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        })
    });
    const websiteSources = websiteSourcesData?.data || [];

    // Dropdown options
    const { data: activeStatusesData } = useQuery({
        queryKey: ['activeStatuses'],
        queryFn: () => leadStatusService.getAll({ isActive: true })
    });
    const activeStatuses = activeStatusesData?.data || [];

    const { data: activeSourcesData } = useQuery({
        queryKey: ['activeSources'],
        queryFn: () => leadSourceService.getAll({ isActive: true })
    });
    const activeSources = activeSourcesData?.data || [];

    // --- Mutations ---
    const createMutation = useMutation({
        mutationFn: (data) => websiteSourceService.create(data),
        onSuccess: (response) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message || 'Website source created successfully');
                queryClient.invalidateQueries(['websiteSources']);
                closeModal();
            } else {
                toast.error(response.message || 'Failed to create website source');
            }
        },
        onError: (error) => {
            const errorMsg = error.message || 'Failed to create website source';
            setFormErrors({ name: errorMsg });
            toast.error(errorMsg);
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => websiteSourceService.update(id, data),
        onSuccess: (response) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message || 'Website source updated successfully');
                queryClient.invalidateQueries(['websiteSources']);
                closeModal();
            } else {
                toast.error(response.message || 'Failed to update website source');
            }
        },
        onError: (error) => {
            const errorMsg = error.message || 'Failed to update website source';
            setFormErrors({ name: errorMsg });
            toast.error(errorMsg);
        }
    });

    const toggleStatusMutation = useMutation({
        mutationFn: (id) => websiteSourceService.toggleStatus(id),
        onSuccess: (response) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message || 'Status updated successfully');
                queryClient.invalidateQueries(['websiteSources']);
            } else {
                toast.error(response.message || 'Failed to update status');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update status');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => websiteSourceService.delete(id),
        onSuccess: (response) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message || 'Website source deleted successfully');
                queryClient.invalidateQueries(['websiteSources']);
                closeDeleteModal();
            } else {
                setDeleteError(response.message || 'Failed to delete item');
                toast.error(response.message || 'Failed to delete item');
            }
        },
        onError: (error) => {
            const errorMsg = error.message || 'Failed to delete item';
            setDeleteError(errorMsg);
            toast.error(errorMsg);
        }
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: (ids) => websiteSourceService.bulkDelete(ids),
        onSuccess: (response) => {
            if (response.success) {
                toast.success(response.responseMessage || response.message || 'Selected website sources deleted successfully');
                queryClient.invalidateQueries(['websiteSources']);
                setSelectedRows(new Set());
                setShowBulkDeleteModal(false);
            } else {
                toast.error(response.message || 'Failed to delete selected website sources');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to delete selected website sources');
        }
    });

    const generateApiKey = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-.@#$*!';
        let key = '';
        const len = 32;
        const array = new Uint8Array(len);
        window.crypto.getRandomValues(array);
        for (let i = 0; i < len; i++) {
            key += chars[array[i] % chars.length];
        }
        setFormData(prev => ({ ...prev, apiKey: key }));
        if (formErrors.apiKey) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.apiKey;
                return newErrors;
            });
        }
    };

    // --- Validation & Handlers ---
    const validateForm = () => {
        const errors = {};
        const trimmedName = formData.name?.trim() || '';
        const trimmedCode = formData.code?.trim() || '';
        const trimmedApiKey = formData.apiKey?.trim() || '';

        // Name
        if (!trimmedName) {
            errors.name = 'Name is required';
        } else if (trimmedName.length < NAME_MIN_LENGTH) {
            errors.name = `Name must be at least ${NAME_MIN_LENGTH} characters`;
        } else if (trimmedName.length > NAME_MAX_LENGTH) {
            errors.name = `Name cannot exceed ${NAME_MAX_LENGTH} characters`;
        }

        // Code
        if (!trimmedCode) {
            errors.code = 'Code is required';
        } else if (trimmedCode.length < CODE_MIN_LENGTH) {
            errors.code = `Code must be at least ${CODE_MIN_LENGTH} characters`;
        } else if (trimmedCode.length > CODE_MAX_LENGTH) {
            errors.code = `Code cannot exceed ${CODE_MAX_LENGTH} characters`;
        } else if (!/^[A-Z0-9_-]+$/.test(trimmedCode.toUpperCase())) {
            errors.code = 'Code can only contain uppercase letters, numbers, hyphens, and underscores';
        }

        // Description
        if (formData.description && formData.description.length > DESCRIPTION_MAX_LENGTH) {
            errors.description = `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`;
        }

        // API Key
        if (!trimmedApiKey) {
            errors.apiKey = 'API Key is required';
        } else if (trimmedApiKey.length < API_KEY_MIN_LENGTH) {
            errors.apiKey = `API Key must be at least ${API_KEY_MIN_LENGTH} characters`;
        } else if (trimmedApiKey.length > API_KEY_MAX_LENGTH) {
            errors.apiKey = `API Key cannot exceed ${API_KEY_MAX_LENGTH} characters`;
        }

        // Default Lead Status
        if (!formData.defaultLeadStatus) {
            errors.defaultLeadStatus = 'Default Lead Status is required';
        }

        // Default Lead Source
        if (!formData.defaultLeadSource) {
            errors.defaultLeadSource = 'Default Lead Source is required';
        }

        // Domain validation — must be a bare domain, no protocol or path allowed
        if (formData.domain?.trim()) {
            const domain = formData.domain.trim();
            if (/^https?:\/\//i.test(domain)) {
                errors.domain = 'Domain must not include a protocol (remove "http://" or "https://")';
            } else if (domain.includes('/')) {
                errors.domain = 'Domain must not include a path (e.g. use "example.com" not "example.com/path")';
            } else {
                // Must be a valid hostname: labels separated by dots, each label alphanumeric + hyphens
                const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
                if (!domainRegex.test(domain)) {
                    errors.domain = 'Invalid domain format (e.g. example.com or sub.example.com)';
                }
            }
        }

        // Website URL validation — must be a full URL with http:// or https://
        if (formData.websiteUrl?.trim()) {
            const url = formData.websiteUrl.trim();
            if (!/^https?:\/\//i.test(url)) {
                errors.websiteUrl = 'Website URL must start with "http://" or "https://"';
            } else {
                try {
                    const parsed = new URL(url);
                    if (!parsed.hostname || !parsed.hostname.includes('.')) {
                        errors.websiteUrl = 'Invalid URL format (e.g. https://example.com)';
                    }
                } catch (_) {
                    errors.websiteUrl = 'Invalid URL format (e.g. https://example.com)';
                }
            }
        }

        return errors;
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const submitData = {
            name: formData.name.trim(),
            code: formData.code.trim().toUpperCase(),
            description: formData.description?.trim() || '',
            domain: formData.domain?.trim() || '',
            websiteUrl: formData.websiteUrl?.trim() || '',
            apiKey: formData.apiKey.trim(),
            active: formData.active,
            defaultLeadStatus: formData.defaultLeadStatus || null,
            defaultLeadSource: formData.defaultLeadSource || null
        };

        if (modalType === 'create') {
            createMutation.mutate(submitData);
        } else {
            updateMutation.mutate({ id: editingItem._id || editingItem.id, data: submitData });
        }
    };

    const handleToggleStatus = (item) => {
        toggleStatusMutation.mutate(item._id || item.id);
    };

    // Modal opens
    const openCreateModal = () => {
        setModalType('create');
        setFormData({
            name: '',
            code: '',
            description: '',
            domain: '',
            websiteUrl: '',
            apiKey: '',
            isActive: true,
            active: true,
            defaultLeadStatus: '',
            defaultLeadSource: ''
        });
        setFormErrors({});
        setIsCodeManuallyEdited(false);
        setEditingItem(null);
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setModalType('edit');
        setFormData({
            name: item.name,
            code: item.code || '',
            description: item.description || '',
            domain: item.domain || '',
            websiteUrl: item.websiteUrl || '',
            apiKey: item.apiKey || '',
            isActive: item.active !== undefined ? item.active : true,
            active: item.active !== undefined ? item.active : true,
            defaultLeadStatus: item.defaultLeadStatus?._id || item.defaultLeadStatus || '',
            defaultLeadSource: item.defaultLeadSource?._id || item.defaultLeadSource || ''
        });
        setFormErrors({});
        setIsCodeManuallyEdited(true);
        setEditingItem(item);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormErrors({});
        setIsCodeManuallyEdited(false);
    };

    const openViewModal = (item) => {
        setViewingItem(item);
        setShowApiKey(false);
        setCopiedApiKey(false);
        setShowViewModal(true);
    };

    const closeViewModal = () => {
        setShowViewModal(false);
        setViewingItem(null);
        setCopiedApiKey(false);
    };

    const handleCopyApiKey = () => {
        if (!viewingItem?.apiKey) return;
        navigator.clipboard.writeText(viewingItem.apiKey).then(() => {
            setCopiedApiKey(true);
            setTimeout(() => setCopiedApiKey(false), 2000);
        }).catch(() => {
            toast.error('Failed to copy API key');
        });
    };

    const openDeleteModal = (item) => {
        setDeletingItem(item);
        setDeleteError('');
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletingItem(null);
        setDeleteError('');
    };

    const handleDelete = () => {
        if (!deletingItem) return;
        deleteMutation.mutate(deletingItem._id || deletingItem.id);
    };

    const handleBulkDelete = () => {
        if (selectedRows.size === 0) return;
        bulkDeleteMutation.mutate(Array.from(selectedRows));
    };

    // Table sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
        return sortConfig.direction === 'asc' ?
            <ArrowUp className="h-4 w-4 text-agrosaf-green" /> :
            <ArrowDown className="h-4 w-4 text-agrosaf-green" />;
    };

    const getItemDate = (item) => {
        if (item.createdAt) return new Date(item.createdAt);
        if (item._id) {
            const id = item._id;
            if (typeof id === 'string' && id.length === 24) {
                const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
                return new Date(timestamp);
            }
        }
        return null;
    };

    // Selection helpers
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = paginatedData.map(item => item._id || item.id);
            setSelectedRows(new Set(allIds));
        } else {
            setSelectedRows(new Set());
        }
    };

    const handleSelectRow = (id) => {
        const newSelected = new Set(selectedRows);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedRows(newSelected);
    };

    // Sorting and Pagination logic
    const sortedData = [...websiteSources].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'createdAt') {
            aValue = getItemDate(a)?.getTime() || 0;
            bValue = getItemDate(b)?.getTime() || 0;
        } else if (sortConfig.key === 'updatedAt') {
            aValue = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            bValue = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        } else if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = (bValue || '').toLowerCase();
        } else if (typeof aValue === 'boolean') {
            aValue = aValue ? 1 : 0;
            bValue = bValue ? 1 : 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalItems = sortedData.length;
    const totalPages = Math.ceil(totalItems / pageLimit) || 1;
    let validCurrentPage = currentPage;
    if (validCurrentPage > totalPages) validCurrentPage = totalPages;
    if (validCurrentPage < 1) validCurrentPage = 1;

    const startIndex = (validCurrentPage - 1) * pageLimit;
    const paginatedData = sortedData.slice(startIndex, startIndex + pageLimit);

    // --- Export helpers ---
    const mapDataForExport = (data) => {
        return data.map(item => {
            const createDate = getItemDate(item);
            const updateDate = item.updatedAt ? new Date(item.updatedAt) : null;
            return {
                "ID": item._id || item.id,
                "Website Code": item.code,
                "Website Name": item.name,
                "Domain": item.domain || '-',
                "Website URL": item.websiteUrl || '-',
                "API Key": item.apiKey || '-',
                "Default Lead Status": item.defaultLeadStatus?.name || '-',
                "Default Lead Source": item.defaultLeadSource?.name || '-',
                "Status": (item.active !== undefined ? item.active : item.isActive) ? 'Active' : 'Inactive',
                "Created At": createDate ? formatDate(createDate) : '-',
                "Updated At": updateDate ? formatDate(updateDate) : '-'
            };
        });
    };

    const handleExportClick = () => {
        if (selectedRows.size > 0) {
            const selectedData = websiteSources.filter(item => selectedRows.has(item._id || item.id));
            const exportData = mapDataForExport(selectedData);
            exportToCSV(exportData, `website_master_export`, {
                title: 'Website Master List',
                type: 'Selected Items'
            });
            setSelectedRows(new Set());
        } else {
            setShowExportModal(true);
        }
    };

    const handleExportCurrentPage = () => {
        const exportData = mapDataForExport(paginatedData);
        exportToCSV(exportData, `website_master_export`, {
            title: 'Website Master List',
            type: 'Current Page'
        });
        setShowExportModal(false);
    };

    const handleExportAll = async () => {
        setExportLoading(true);
        try {
            const response = await websiteSourceService.getAll({
                search: searchTerm,
                ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
                limit: 1000
            });
            const allItems = response?.data || [];
            const exportData = mapDataForExport(allItems);
            exportToCSV(exportData, `website_master_export`, {
                title: 'Website Master List',
                type: 'All Filtered Items'
            });
        } catch (error) {
            toast.error('Export failed');
        } finally {
            setExportLoading(false);
            setShowExportModal(false);
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
                        <Globe className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Website Master</h1>
                        <p className="text-sm sm:text-base text-slate-500 mt-1">
                            Manage website integration channels, domain details, default lead mappings, and API keys.
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {/* Search & Actions Bar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                    <div className="flex-1 w-full flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search website sources..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10 pr-10 w-full"
                            />
                            {searchTerm && (
                                <button
                                    onClick={handleClearSearch}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="input-field w-auto min-w-[150px]"
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                        <DateRangeFilter
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            onChange={setDateRange}
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {selectedRows.size > 0 && (
                            <button
                                onClick={() => setShowBulkDeleteModal(true)}
                                className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
                            >
                                <Trash2 className="h-4 w-4" />
                                <span>Delete ({selectedRows.size})</span>
                            </button>
                        )}
                        <button
                            onClick={handleExportClick}
                            disabled={exportLoading}
                            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-slate-700" /> : <Download className="h-4 w-4" />}
                            <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
                        </button>
                        <button
                            onClick={openCreateModal}
                            className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add Website Source</span>
                        </button>
                    </div>
                </div>

                {/* Table Section */}
                {loadingWebsiteSources ? (
                    <div className="flex items-center justify-center py-12">
                        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
                        <span className="ml-3 text-gray-600">Loading website sources...</span>
                    </div>
                ) : paginatedData.length === 0 ? (
                    <div className="text-center py-12">
                        {searchTerm ? (
                            <>
                                <SearchX className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
                                <p className="text-gray-600 mb-4">No website sources found matching "{searchTerm}"</p>
                                <Button variant="outline" onClick={handleClearSearch} className="text-gray-600">
                                    Clear Search
                                </Button>
                            </>
                        ) : (
                            <>
                                <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No Website Sources Found</h3>
                                <p className="text-gray-600 mb-4">Create your first website configuration to get started.</p>
                                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={openCreateModal} className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white">
                                    Add Website Source
                                </Button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-4">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                                            checked={paginatedData.length > 0 && paginatedData.every(item => selectedRows.has(item._id || item.id))}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group" onClick={() => handleSort('code')}>
                                        <div className="flex items-center space-x-1">
                                            <span>Code</span>
                                            {getSortIndicator('code')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group" onClick={() => handleSort('name')}>
                                        <div className="flex items-center space-x-1">
                                            <span>Name</span>
                                            {getSortIndicator('name')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group" onClick={() => handleSort('domain')}>
                                        <div className="flex items-center space-x-1">
                                            <span>Domain</span>
                                            {getSortIndicator('domain')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <span>Integration Details</span>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <span>API Key</span>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group" onClick={() => handleSort('active')}>
                                        <div className="flex items-center space-x-1">
                                            <span>Status</span>
                                            {getSortIndicator('active')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group" onClick={() => handleSort('createdAt')}>
                                        <div className="flex items-center space-x-1">
                                            <span>Created At</span>
                                            {getSortIndicator('createdAt')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedData.map((item) => {
                                    const isActive = item.active !== undefined ? item.active : item.isActive;
                                    return (
                                        <tr key={item._id || item.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                                                    checked={selectedRows.has(item._id || item.id)}
                                                    onChange={() => handleSelectRow(item._id || item.id)}
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded inline-block max-w-[150px] truncate" title={item.code}>
                                                    {item.code}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 max-w-[150px] truncate capitalize" title={item.name}>{item.name}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{item.domain || '-'}</div>
                                                {item.websiteUrl && <a href={item.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0F2942] hover:underline">{item.websiteUrl}</a>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-xs text-gray-950 font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded inline-block mb-1 capitalize">
                                                    Status: {item.defaultLeadStatus?.name || '-'}
                                                </div>
                                                <br />
                                                <div className="text-xs text-gray-950 font-medium bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded inline-block capitalize">
                                                    Source: {item.defaultLeadSource?.name || '-'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <span className="font-mono bg-gray-50 px-2 py-1 border rounded" title={item.apiKey}>
                                                    ••••••••{item.apiKey ? item.apiKey.slice(-4) : ''}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {formatDate(getItemDate(item)) || '-'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center space-x-3">
                                                    <button onClick={() => openViewModal(item)} className="text-gray-600 hover:text-gray-900" title="View Details">
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                    <button onClick={() => openEditModal(item)} className="text-[#0F2942] hover:text-blue-900" title="Edit">
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button onClick={() => handleToggleStatus(item)} className={isActive ? 'text-orange-500 hover:text-orange-700' : 'text-green-600 hover:text-green-800'} title={isActive ? 'Deactivate' : 'Activate'}>
                                                        {isActive ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                                                    </button>
                                                    <button onClick={() => openDeleteModal(item)} className="text-red-600 hover:text-red-900" title="Delete">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PaginationControls */}
                {paginatedData.length > 0 && !loadingWebsiteSources && (
                    <div className="mt-6">
                        <PaginationControls
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            limit={pageLimit}
                            onPageChange={setCurrentPage}
                            onLimitChange={(newLimit) => {
                                setPageLimit(newLimit);
                                setCurrentPage(1);
                            }}
                            limitOptions={[10, 25, 50, 100]}
                        />
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 overflow-y-auto !mt-0">
                    <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col shadow-xl">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900 capitalize">
                                {modalType === 'create' ? 'Add Website Source' : 'Edit Website Source'}
                            </h3>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={handleFormSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Website Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => {
                                            const newName = e.target.value;
                                            setFormData(prev => {
                                                const updated = { ...prev, name: newName };
                                                if (modalType === 'create' && !isCodeManuallyEdited) {
                                                    updated.code = newName.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_-]/g, '');
                                                }
                                                return updated;
                                            });
                                        }}
                                        className={`input-field w-full ${formErrors.name ? 'border-red-500' : ''}`}
                                        placeholder="e.g. Main E-commerce Site"
                                        maxLength={100}
                                    />
                                    <div className="flex justify-between items-start mt-1">
                                        <div className="flex-1">
                                            {formErrors.name && (
                                                <p className="text-xs text-red-500">{formErrors.name}</p>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-400 ml-2">
                                            {formData.name?.length || 0}/100
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Website Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.code}
                                        onChange={(e) => {
                                            setIsCodeManuallyEdited(true);
                                            setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') });
                                        }}
                                        className={`input-field w-full uppercase ${formErrors.code ? 'border-red-500' : ''}`}
                                        placeholder="e.g. MAIN_SITE"
                                        disabled={modalType === 'edit'}
                                        maxLength={100}
                                    />
                                    <div className="flex justify-between items-start mt-1">
                                        <div className="flex-1">
                                            {formErrors.code && (
                                                <p className="text-xs text-red-500">{formErrors.code}</p>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-400 ml-2">
                                            {formData.code?.length || 0}/100
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className={`input-field w-full h-20 resize-none ${formErrors.description ? 'border-red-500' : ''}`}
                                    placeholder="Optional description of this website source..."
                                    maxLength={1000}
                                />
                                <div className="flex justify-between items-start mt-1">
                                    <div className="flex-1">
                                        {formErrors.description && (
                                            <p className="text-xs text-red-500">{formErrors.description}</p>
                                        )}
                                    </div>
                                    <span className="text-xs text-gray-400 ml-2">
                                        {formData.description?.length || 0}/1000
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Domain Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.domain}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setFormData(prev => ({ ...prev, domain: val }));
                                            setFormErrors(prev => {
                                                const trimmed = val.trim();
                                                if (!trimmed) {
                                                    const { domain: _, ...rest } = prev;
                                                    return rest;
                                                }
                                                let err;
                                                if (/^https?:\/\//i.test(trimmed)) {
                                                    err = 'Domain must not include a protocol (remove "http://" or "https://")';
                                                } else if (trimmed.includes('/')) {
                                                    err = 'Domain must not include a path (e.g. use "example.com" not "example.com/path")';
                                                } else if (!/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(trimmed)) {
                                                    err = 'Invalid domain format (e.g. example.com or sub.example.com)';
                                                }
                                                return err ? { ...prev, domain: err } : (() => { const { domain: _, ...rest } = prev; return rest; })();
                                            });
                                        }}
                                        className={`input-field w-full ${formErrors.domain ? 'border-red-500' : ''}`}
                                        placeholder="e.g. example.com"
                                    />
                                    {formErrors.domain ? (
                                        <p className="mt-1 text-xs text-red-500">{formErrors.domain}</p>
                                    ) : (
                                        <p className="mt-1 text-xs text-gray-400">Bare domain only — no http:// or paths</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Website URL
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.websiteUrl}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setFormData(prev => ({ ...prev, websiteUrl: val }));
                                            setFormErrors(prev => {
                                                const trimmed = val.trim();
                                                if (!trimmed) {
                                                    const { websiteUrl: _, ...rest } = prev;
                                                    return rest;
                                                }
                                                let err;
                                                if (!/^https?:\/\//i.test(trimmed)) {
                                                    err = 'Website URL must start with "http://" or "https://"';
                                                } else {
                                                    try {
                                                        const parsed = new URL(trimmed);
                                                        if (!parsed.hostname || !parsed.hostname.includes('.')) {
                                                            err = 'Invalid URL format (e.g. https://example.com)';
                                                        }
                                                    } catch (_) {
                                                        err = 'Invalid URL format (e.g. https://example.com)';
                                                    }
                                                }
                                                return err ? { ...prev, websiteUrl: err } : (() => { const { websiteUrl: _, ...rest } = prev; return rest; })();
                                            });
                                        }}
                                        className={`input-field w-full ${formErrors.websiteUrl ? 'border-red-500' : ''}`}
                                        placeholder="e.g. https://example.com"
                                    />
                                    {formErrors.websiteUrl ? (
                                        <p className="mt-1 text-xs text-red-500">{formErrors.websiteUrl}</p>
                                    ) : (
                                        <p className="mt-1 text-xs text-gray-400">Full URL including https://</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    API Key <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={formData.apiKey}
                                        onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                                        className={`input-field flex-1 font-mono ${formErrors.apiKey ? 'border-red-500' : ''}`}
                                        placeholder="Unique API Key for integration authentication"
                                        maxLength={100}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={generateApiKey}
                                        className="whitespace-nowrap flex items-center gap-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                                        icon={<RefreshCw className="h-4 w-4" />}
                                    >
                                        Generate
                                    </Button>
                                </div>
                                <div className="flex justify-between items-start mt-1">
                                    <div className="flex-1">
                                        {formErrors.apiKey && (
                                            <p className="text-xs text-red-500">{formErrors.apiKey}</p>
                                        )}
                                    </div>
                                    <span className="text-xs text-gray-400 ml-2">
                                        {formData.apiKey?.length || 0}/100
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Default Lead Status <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={formData.defaultLeadStatus}
                                        onChange={(e) => setFormData({ ...formData, defaultLeadStatus: e.target.value })}
                                        className={`input-field w-full ${formErrors.defaultLeadStatus ? 'border-red-500' : ''}`}
                                    >
                                        <option value="">Select Lead Status</option>
                                        {activeStatuses.map(status => (
                                            <option key={status._id || status.id} value={status._id || status.id}>
                                                {status.name}
                                            </option>
                                        ))}
                                    </select>
                                    {formErrors.defaultLeadStatus && (
                                        <p className="mt-1 text-xs text-red-500">{formErrors.defaultLeadStatus}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Default Lead Source <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={formData.defaultLeadSource}
                                        onChange={(e) => setFormData({ ...formData, defaultLeadSource: e.target.value })}
                                        className={`input-field w-full ${formErrors.defaultLeadSource ? 'border-red-500' : ''}`}
                                    >
                                        <option value="">Select Lead Source</option>
                                        {activeSources.map(source => (
                                            <option key={source._id || source.id} value={source._id || source.id}>
                                                {source.name}
                                            </option>
                                        ))}
                                    </select>
                                    {formErrors.defaultLeadSource && (
                                        <p className="mt-1 text-xs text-red-500">{formErrors.defaultLeadSource}</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center pt-2">
                                <input
                                    type="checkbox"
                                    id="active"
                                    checked={formData.active}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                    className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                                />
                                <label htmlFor="active" className="ml-2 block text-sm text-gray-950 font-medium cursor-pointer">
                                    Active (Receive leads from this website source)
                                </label>
                            </div>

                            <div className="pt-4 border-t border-gray-200 flex justify-end gap-2">
                                <Button variant="outline" type="button" onClick={closeModal} className="text-gray-600">
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white"
                                    disabled={createMutation.isLoading || updateMutation.isLoading}
                                >
                                    {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save Website Source'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Details Modal */}
            {showViewModal && viewingItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 !mt-0">
                    <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] shadow-xl flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-55 flex-shrink-0">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <Globe className="h-5 w-5 text-agrosaf-green" />
                                Website Source Details
                            </h3>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto flex-1">
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4 border border-gray-100">
                                <div>
                                    <span className="block text-xs font-semibold text-gray-500 uppercase">Website Name</span>
                                    <span className="text-sm font-medium text-gray-900 capitalize">{viewingItem.name}</span>
                                </div>
                                <div>
                                    <span className="block text-xs font-semibold text-gray-500 uppercase">Website Code</span>
                                    <span className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-0.5 rounded inline-block break-all">{viewingItem.code}</span>
                                </div>
                            </div>

                            <div>
                                <span className="block text-xs font-semibold text-gray-500 uppercase">Description</span>
                                <span className="text-sm text-gray-700 first-letter:capitalize">{viewingItem.description || 'No description provided'}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="block text-xs font-semibold text-gray-500 uppercase">Domain</span>
                                    <span className="text-sm text-gray-900">{viewingItem.domain || '-'}</span>
                                </div>
                                <div>
                                    <span className="block text-xs font-semibold text-gray-500 uppercase">Website URL</span>
                                    {viewingItem.websiteUrl ? (
                                        <a href={viewingItem.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#0F2942] hover:underline">
                                            {viewingItem.websiteUrl}
                                        </a>
                                    ) : '-'}
                                </div>
                            </div>

                            <div className="border-t border-gray-200 pt-4 space-y-3">
                                <div>
                                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">API Key (Integration Secret)</span>
                                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded px-3 py-2">
                                        <Key className="h-4 w-4 text-gray-400" />
                                        <span className="font-mono text-sm flex-1 truncate">
                                            {showApiKey ? viewingItem.apiKey : '••••••••••••••••••••••••'}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowApiKey(!showApiKey)}
                                            className="py-1 px-2 text-xs"
                                        >
                                            {showApiKey ? 'Hide' : 'Reveal'}
                                        </Button>
                                        <button
                                            type="button"
                                            onClick={handleCopyApiKey}
                                            title={copiedApiKey ? 'Copied!' : 'Copy API Key'}
                                            className={`p-1.5 rounded border transition-colors ${copiedApiKey ? 'border-green-300 bg-green-50 text-green-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
                                        >
                                            {copiedApiKey ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 border-t border-gray-200 pt-4">
                                <div>
                                    <span className="block text-xs font-semibold text-gray-500 uppercase">Default Status</span>
                                    <span className="text-sm font-medium text-gray-900 bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded inline-block mt-1 capitalize">
                                        {viewingItem.defaultLeadStatus?.name || '-'}
                                    </span>
                                </div>
                                <div>
                                    <span className="block text-xs font-semibold text-gray-500 uppercase">Default Source</span>
                                    <span className="text-sm font-medium text-gray-900 bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded inline-block mt-1 capitalize">
                                        {viewingItem.defaultLeadSource?.name || '-'}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 border-t border-gray-200 pt-4 text-xs text-gray-500">
                                <div>
                                    <span className="block font-semibold">Status</span>
                                    <span className={`inline-block mt-1 font-medium ${(viewingItem.active !== undefined ? viewingItem.active : viewingItem.isActive) ? 'text-green-700' : 'text-gray-500'}`}>
                                        {(viewingItem.active !== undefined ? viewingItem.active : viewingItem.isActive) ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div>
                                    <span className="block font-semibold">Leads Count</span>
                                    <span className="text-sm font-bold text-gray-900 mt-0.5 block">{viewingItem.totalLeadsReceived || 0}</span>
                                </div>
                                <div>
                                    <span className="block font-semibold">Created Date</span>
                                    <span className="block mt-1">{formatDate(getItemDate(viewingItem)) || '-'}</span>
                                </div>
                            </div>

                            {/* Created By and Updated By Section */}
                            <div className="border-t border-gray-200 pt-4 space-y-4">
                                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Info className="h-4 w-4 text-[#0F2942]" />
                                        <span className="text-xs font-semibold text-blue-800 uppercase">Created By</span>
                                    </div>
                                    {viewingItem.createdBy ? (
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                            <div>
                                                <span className="block text-gray-500">Name</span>
                                                <span className="font-medium text-gray-900">
                                                    {viewingItem.createdBy.fullName || 
                                                     (viewingItem.createdBy.firstName 
                                                        ? `${viewingItem.createdBy.firstName} ${viewingItem.createdBy.lastName || ''}`.trim() 
                                                        : viewingItem.createdBy.name || '-')}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Employee ID</span>
                                                <span className="font-medium text-gray-900">{viewingItem.createdBy.employeeId || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Email</span>
                                                <span className="font-medium text-gray-900">{viewingItem.createdBy.email || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Role</span>
                                                <span className="font-medium text-gray-900">{viewingItem.createdBy.role || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Department</span>
                                                <span className="font-medium text-gray-900">{viewingItem.createdBy.department || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Timestamp</span>
                                                <span className="font-medium text-gray-900">{formatDate(getItemDate(viewingItem)) || '-'}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-500">No creator information available</span>
                                    )}
                                </div>

                                <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Info className="h-4 w-4 text-green-600" />
                                        <span className="text-xs font-semibold text-green-800 uppercase">Updated By</span>
                                    </div>
                                    {viewingItem.updatedBy ? (
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                            <div>
                                                <span className="block text-gray-500">Name</span>
                                                <span className="font-medium text-gray-900">
                                                    {viewingItem.updatedBy.fullName || 
                                                     (viewingItem.updatedBy.firstName 
                                                        ? `${viewingItem.updatedBy.firstName} ${viewingItem.updatedBy.lastName || ''}`.trim() 
                                                        : viewingItem.updatedBy.name || '-')}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Employee ID</span>
                                                <span className="font-medium text-gray-900">{viewingItem.updatedBy.employeeId || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Email</span>
                                                <span className="font-medium text-gray-900">{viewingItem.updatedBy.email || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Role</span>
                                                <span className="font-medium text-gray-900">{viewingItem.updatedBy.role || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Department</span>
                                                <span className="font-medium text-gray-900">{viewingItem.updatedBy.department || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="block text-gray-500">Timestamp</span>
                                                <span className="font-medium text-gray-900">{viewingItem.updatedAt ? formatDate(new Date(viewingItem.updatedAt)) : '-'}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-500">No updater information available</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
                            <Button variant="primary" onClick={closeViewModal} className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white">
                                Close Details
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Single Delete Confirmation Modal */}
            {showDeleteModal && deletingItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 !mt-0">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl space-y-4">
                        <div className="flex items-center space-x-3 text-red-600">
                            <AlertTriangle className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Delete Website Source</h3>
                        </div>
                        <p className="text-gray-600 text-sm">
                            Are you sure you want to delete the website source <strong className="text-gray-900">"{deletingItem.name}"</strong>? This action cannot be undone and will stop API lead processing.
                        </p>
                        {deleteError && (
                            <div className="bg-red-50 border border-red-200 text-red-600 rounded p-3 text-xs">
                                {deleteError}
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={closeDeleteModal} className="text-gray-600">
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                className="btn-danger"
                                disabled={deleteMutation.isLoading}
                            >
                                {deleteMutation.isLoading ? 'Deleting...' : 'Delete Permanently'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 !mt-0">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl space-y-4">
                        <div className="flex items-center space-x-3 text-red-600">
                            <AlertTriangle className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Delete Selected Items</h3>
                        </div>
                        <p className="text-gray-600 text-sm">
                            Are you sure you want to delete <strong className="text-gray-900">{selectedRows.size}</strong> selected website sources? This will permanently disable their integration.
                        </p>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setShowBulkDeleteModal(false)} className="text-gray-600">
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleBulkDelete}
                                className="btn-danger"
                                disabled={bulkDeleteMutation.isLoading}
                            >
                                {bulkDeleteMutation.isLoading ? 'Deleting...' : 'Delete Selected'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Export Selection Modal */}
            {showExportModal && (
                <ExportModal
                    isOpen={showExportModal}
                    onClose={() => setShowExportModal(false)}
                    onExportCurrent={handleExportCurrentPage}
                    onExportAll={handleExportAll}
                    isLoading={exportLoading}
                />
            )}
        </div>
    );
}
